import { useState, useEffect } from 'react'
import { X, Search, Loader, AlertTriangle } from 'lucide-react'
import { isAddress as viemIsAddress } from 'viem'
import { type Token } from '@/config'
import { useChainConfig } from '@/hooks/useChainConfig'
import { useTokenBalance } from '@/hooks/useTokenBalance'
import { ERC20_ABI } from '@/abi/ERC20'
import { useCustomTokensStore } from '@/store/customTokens'

interface TokenSelectorProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (token: Token) => void
  selectedToken?: Token
}

export function TokenSelector({ isOpen, onClose, onSelect, selectedToken }: TokenSelectorProps) {
  const [search, setSearch] = useState('')
  const [importedToken, setImportedToken] = useState<Token | null>(null)
  const [importing, setImporting] = useState(false)
  const [importConfirmed, setImportConfirmed] = useState(false)
  const { defaultTokens, viemClient, chainId } = useChainConfig()
  const { addToken, getTokens } = useCustomTokensStore()
  const customTokens = getTokens(chainId)
  // All tokens: default + saved custom tokens (deduplicated)
  const allTokens = [
    ...defaultTokens,
    ...customTokens.filter(
      (ct) => !defaultTokens.some((dt) => dt.address.toLowerCase() === ct.address.toLowerCase())
    ),
  ]

  // When search looks like a contract address, fetch token info from chain
  useEffect(() => {
    setImportedToken(null)
    setImportConfirmed(false)
    if (!viemIsAddress(search)) return

    // Check if it's already in the list
    const existing = allTokens.find(
      (t) => t.address.toLowerCase() === search.toLowerCase()
    )
    if (existing) return

    let cancelled = false
    setImporting(true)

    Promise.all([
      viemClient.readContract({ address: search as `0x${string}`, abi: ERC20_ABI, functionName: 'symbol' }),
      viemClient.readContract({ address: search as `0x${string}`, abi: ERC20_ABI, functionName: 'name' }),
      viemClient.readContract({ address: search as `0x${string}`, abi: ERC20_ABI, functionName: 'decimals' }),
    ])
      .then(([symbol, name, decimals]) => {
        if (cancelled) return
        setImportedToken({
          address: search as `0x${string}`,
          symbol: symbol as string,
          name: name as string,
          decimals: decimals as number,
        })
      })
      .catch(() => { /* not a valid ERC20 */ })
      .finally(() => { if (!cancelled) setImporting(false) })

    return () => { cancelled = true }
  }, [search, viemClient, chainId, defaultTokens])

  if (!isOpen) return null

  const filtered = allTokens.filter(
    (t) =>
      t.symbol.toLowerCase().includes(search.toLowerCase()) ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.address.toLowerCase() === search.toLowerCase()
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl p-0 overflow-hidden"
        style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Select a token
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
            <Search size={18} style={{ color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Search by name or paste address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: 'var(--text-primary)' }}
              autoFocus
            />
          </div>
        </div>

        {/* Quick select */}
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {allTokens.slice(0, 6).map((token) => (
            <button
              key={token.symbol}
              onClick={() => { onSelect(token); onClose() }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors hover:bg-[var(--bg-tertiary)]"
              style={{
                border: '1px solid var(--border)',
                color: selectedToken?.symbol === token.symbol ? 'var(--text-primary)' : 'var(--text-secondary)',
                backgroundColor: selectedToken?.symbol === token.symbol ? 'var(--bg-tertiary)' : 'transparent',
              }}
            >
              <TokenIcon symbol={token.symbol} size={20} />
              {token.symbol}
            </button>
          ))}
        </div>

        {/* Token list */}
        <div className="max-h-72 overflow-y-auto px-2 pb-4">
          {importing && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <Loader size={16} className="animate-spin" />
              Loading token info...
            </div>
          )}
          {!importing && importedToken && (
            <>
              <div className="px-3 py-2 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                Found via address
              </div>
              <div className="mx-2 p-4 rounded-2xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid rgba(255, 180, 0, 0.25)' }}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="mt-0.5" style={{ color: '#FFB400' }}>
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                      Import custom token
                    </div>
                    <div className="text-xs leading-5" style={{ color: 'var(--text-secondary)' }}>
                      Anyone can deploy a token with any name or symbol. Only import this token if you trust the contract address.
                    </div>
                  </div>
                </div>

                <TokenRow
                  key={importedToken.address}
                  token={importedToken}
                  isSelected={selectedToken?.address?.toLowerCase() === importedToken.address.toLowerCase()}
                  onSelect={() => {}}
                  disableHover
                />

                <div className="mt-3 px-3 py-2 rounded-xl break-all text-xs" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                  {importedToken.address}
                </div>

                <label className="mt-3 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={importConfirmed}
                    onChange={(e) => setImportConfirmed(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="text-xs leading-5" style={{ color: 'var(--text-secondary)' }}>
                    I understand that imported tokens may be fake, malicious, or illiquid.
                  </span>
                </label>

                <button
                  onClick={() => {
                    if (!importConfirmed) return
                    addToken(chainId, importedToken)
                    onSelect(importedToken)
                    onClose()
                  }}
                  disabled={!importConfirmed}
                  className="mt-4 w-full py-3 rounded-2xl font-semibold text-sm transition-all"
                  style={{
                    backgroundColor: importConfirmed ? 'var(--accent)' : 'var(--bg-secondary)',
                    color: importConfirmed ? 'var(--bg-primary)' : 'var(--text-secondary)',
                    cursor: importConfirmed ? 'pointer' : 'default',
                    border: '1px solid var(--border)',
                  }}
                >
                  Import Token
                </button>
              </div>
            </>
          )}
          {!importing && !importedToken && filtered.map((token) => (
            <TokenRow
              key={token.symbol}
              token={token}
              isSelected={selectedToken?.symbol === token.symbol}
              onSelect={() => { onSelect(token); onClose() }}
            />
          ))}
          {!importing && filtered.length === 0 && !importedToken && (
            <div className="text-center py-8 text-sm" style={{ color: 'var(--text-secondary)' }}>
              No tokens found
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TokenRow({ token, isSelected, onSelect, disableHover = false }: { token: Token; isSelected: boolean; onSelect: () => void; disableHover?: boolean }) {
  const { formatted } = useTokenBalance(token)

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${disableHover ? '' : 'hover:bg-[var(--bg-tertiary)]'}`}
      style={{ color: 'var(--text-primary)' }}
    >
      <TokenIcon symbol={token.symbol} size={36} />
      <div className="text-left">
        <div className="font-medium text-sm">{token.symbol}</div>
        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{token.name}</div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {formatted && (
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{formatted}</span>
        )}
        {isSelected && (
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--success)' }} />
        )}
      </div>
    </button>
  )
}

export function TokenIcon({ symbol, size = 24 }: { symbol: string; size?: number }) {
  const colors: Record<string, string> = {
    tBNB: '#F0B90B',
    WBNB: '#F0B90B',
    USDC: '#2775CA',
    WUSDC: '#2775CA',
    AXON: '#FFFFFF',
    mUSDC: '#2775CA',
    mUSDT: '#26A17B',
    mWBTC: '#F7931A',
    mWETH: '#627EEA',
  }
  const color = colors[symbol] || '#999'

  return (
    <div
      className="rounded-full flex items-center justify-center font-bold shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: `${color}22`,
        color: color,
        fontSize: size * 0.35,
        border: `1.5px solid ${color}44`,
      }}
    >
      {symbol.charAt(0)}
    </div>
  )
}
